import React from 'react';
import { IdsButton, IdsSectionNotice } from '@investec/ids-web-react';
import Modal from '../Modal/Modal';

import './isaTransferFaqModals.scss';

interface IsaTransferFaqModalsProps {
    providers: Array<{ id: string; name: string }>;
    showProviderNotListed: boolean;
    onCloseProviderNotListed: () => void;
    showISATransfer: boolean;
    onCloseISATransfer: () => void;
}

const IsaTransferFaqModals: React.FC<IsaTransferFaqModalsProps> = ({
    providers = [],
    showProviderNotListed,
    onCloseProviderNotListed,
    showISATransfer,
    onCloseISATransfer,
}) => {
    const ModalFooter: React.FC<{ onClose: () => void }> = ({ onClose }) => (
        <div className="isa-faq-modal__footer">
            <div className="isa-faq-modal__footer-divider" />
            <div className="isa-faq-modal__footer-actions">
                <IdsButton
                    type="button"
                    appearance="default"
                    size="md"
                    className="isa-faq-modal__back-btn"
                    onClick={onClose}
                >
                    Back
                </IdsButton>
            </div>
        </div>
    );

    return (
        <>
            {showProviderNotListed && (
                <Modal onClose={onCloseProviderNotListed} className="isa-faq-modal">
                    <div className="isa-faq-modal__content">
                        <h2 className="isa-faq-modal__title">What if my provider isn&apos;t listed?</h2>
                        <p className="isa-faq-modal__body">
                            We can only accept Cash ISA transfers from the providers listed below. If yours isn&apos;t listed, you cannot transfer your Cash ISA to us. However, you can still open and fund a new ISA from your linked account or Online Flexi Saver if you have unused allowance this tax year.
                        </p>
                        <p className="isa-faq-modal__subtitle">
                            We can accept transfers from:
                        </p>
                        <ul className="isa-faq-modal__providers">
                            {providers.map((provider) => (
                                <li key={provider.id} className="isa-faq-modal__provider-item">
                                    {provider.name}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <ModalFooter onClose={onCloseProviderNotListed} />
                </Modal>
            )}

            {showISATransfer && (
                <Modal onClose={onCloseISATransfer} className="isa-faq-modal">
                    <div className="isa-faq-modal__content">
                        <h2 className="isa-faq-modal__title">How does the ISA transfer work?</h2>
                        <p className="isa-faq-modal__body">
                            Transferring your Cash ISA to Investec Save is straightforward, we&apos;ll manage the process for you.
                        </p>
                        <div className="isa-faq-modal__steps">
                            <div className="isa-faq-modal__step">
                                <div className="isa-faq-modal__step-left">
                                    <div className="isa-faq-modal__step-circle" />
                                    <div className="isa-faq-modal__step-line" />
                                </div>
                                <div className="isa-faq-modal__step-right">
                                    <strong className="isa-faq-modal__step-title">1. Tell us about your existing Cash ISA</strong>
                                    <p className="isa-faq-modal__step-body">Choose your current provider from the list and let us know whether you&apos;d like to transfer the full amount or a partial amount.</p>
                                    <IdsSectionNotice appearance="information" className="isa-faq-modal__step-info">
                                        If your provider isn&apos;t listed, it means we&apos;re not able to accept a transfer from them.
                                    </IdsSectionNotice>
                                </div>
                            </div>
                            <div className="isa-faq-modal__step">
                                <div className="isa-faq-modal__step-left">
                                    <div className="isa-faq-modal__step-circle" />
                                    <div className="isa-faq-modal__step-line" />
                                </div>
                                <div className="isa-faq-modal__step-right">
                                    <strong className="isa-faq-modal__step-title">2. We contact your provider</strong>
                                    <p className="isa-faq-modal__step-body">Once you submit your application, we&apos;ll request the transfer on your behalf. You don&apos;t need to close your existing Cash ISA yourself.</p>
                                </div>
                            </div>
                            <div className="isa-faq-modal__step">
                                <div className="isa-faq-modal__step-left">
                                    <div className="isa-faq-modal__step-circle" />
                                    <div className="isa-faq-modal__step-line" />
                                </div>
                                <div className="isa-faq-modal__step-right">
                                    <strong className="isa-faq-modal__step-title">3. Your money moves securely</strong>
                                    <p className="isa-faq-modal__step-body">Your current provider sends the funds directly to us. Transfers in can take up to 15 business days, depending on your provider.</p>
                                </div>
                            </div>
                            <div className="isa-faq-modal__step">
                                <div className="isa-faq-modal__step-left">
                                    <div className="isa-faq-modal__step-circle" />
                                </div>
                                <div className="isa-faq-modal__step-right">
                                    <strong className="isa-faq-modal__step-title">4. We&apos;ll keep you informed</strong>
                                    <p className="isa-faq-modal__step-body">We&apos;ll let you know as soon as your transfer is complete and your funds start earning interest in your new Investec Cash ISA.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <ModalFooter onClose={onCloseISATransfer} />
                </Modal>
            )}
        </>
    );
};

export default IsaTransferFaqModals;